
import { IFileSystemEntry } from '../fileSystem/IFileSystem';
import { IFile } from '../fileSystem/IFile';
import { buildEntriesFromEntryList, buildEntriesFromFileList, IEntry } from '../fileSystem/FileEntries';

type IDataTransferItem = DataTransferItem & {
  getAsEntry?: () => IFileSystemEntry;
  webkitGetAsEntry?: () => IFileSystemEntry;
};

/**
 * @public
 */
export interface IFileDropData {
  entries: IEntry[];
}

/**
 * Get the drag data (file contents) for a drag from the desktop
 * @public
 */
export function getFileDropEntries(event: React.DragEvent<{}>): IEntry[] {
  let transferItems: DataTransferItemList | undefined;
  let transferFiles: FileList | undefined;

  try {
    transferItems = event.dataTransfer.items;
    transferFiles = event.dataTransfer.files;
  } catch (e) {
    // Certain browsers will throw if event.dataTransfer.items is accessed in some cases because of security concerns
    // We can't do anything about these cases at this point so we will just have to ignore the drag
    window.setTimeout(() => {
      // The error must not be allowed to propagate, but it still needs to be visible.
      // Rethrow on a timeout for the moment until a general error handler is brought in.
      throw e;
    }, 0);
  }

  const files: IFile[] = [];
  const fileSystemEntries: IFileSystemEntry[] = [];

  if (transferItems && transferItems.length) {
    // convert DataTransferItemList to entries or files because items are only available while the drop event is active
    for (let i = 0; i < transferItems.length; i++) {
      // TypeScript doesn't know about all the properties on a DataTransferItem yet
      const item: IDataTransferItem = transferItems[i];

      if (item.getAsEntry) {
        fileSystemEntries.push(item.getAsEntry());
      } else if (item.webkitGetAsEntry) {
        fileSystemEntries.push(item.webkitGetAsEntry());
      } else if (typeof item.getAsFile === 'function') {
        const file = item.getAsFile();

        if (file) {
          files.push(file);
        }
      }
    }
  } else if (transferFiles && transferFiles.length > 0) {
    for (let i = 0, length = transferFiles.length; i < length; i++) {
      files.push(transferFiles[i]);
    }
  }

  return [...buildEntriesFromEntryList(fileSystemEntries), ...buildEntriesFromFileList(files)];
}

/**
 * Returns true if the event is a FileDrop event
 * @public
 */
export function isFileDropEvent(event: React.DragEvent<{}>): boolean {
  const {
    dataTransfer
  } = event;

  if (dataTransfer) {
    const {
      files,
      types
    } = dataTransfer;

    if (files && files.length > 0) {
      return true;
    } else {
      if (types) {
        return types.some((type: string) => type.toLowerCase() === 'files');
      }
    }
  }

  return false;
}
